import { useEffect, useMemo, useState } from 'react';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { MetaApprovalStatusDefinition } from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useHasActiveWhatsAppLocaleMatchingStatus } from 'modules/templateEditor/hooks/useHasActiveWhatsAppLocaleMatchingStatus';

type EditableWhatsApp = {
  /** Whether the WhatsApp template is editable. */
  isEditableWhatsAppTemplate: boolean;
};

export const useEditableWhatsApp = (): EditableWhatsApp => {
  const { liveTemplate } = useEditableFieldsContext();
  const { activeWhatsAppTemplateLocales: locales } = useLocalesQueryContext();

  const [isEditableWhatsAppTemplate, setIsEditableWhatsAppTemplate] = useState<boolean>(false);

  const mediumData = liveTemplate?.mediumData || {};
  const metaApprovalStatus =
    'metaApprovalStatus' in mediumData ? mediumData.metaApprovalStatus : undefined;

  /** If at least one locale for a template has been rejected, the template should not be editable for ANY locales. */
  const hasRejectedLocale = useHasActiveWhatsAppLocaleMatchingStatus(
    locales,
    MetaApprovalStatusDefinition.Rejected,
  );

  const hasEditableWhatsAppStatus = useMemo(() => {
    /** User-initiated WhatsApp templates have a `null` metaApprovalStatus and should not be blocked from editing. */
    if (metaApprovalStatus === null) return true;

    /** Approval status not known. Opt for stricter editing requirements. */
    if (metaApprovalStatus === undefined) return false;

    const editableStatuses = new Set([MetaApprovalStatusDefinition.NotSubmitted]);

    return editableStatuses.has(metaApprovalStatus);
  }, [metaApprovalStatus]);

  useEffect(() => {
    setIsEditableWhatsAppTemplate(hasEditableWhatsAppStatus && !hasRejectedLocale);
  }, [hasEditableWhatsAppStatus, hasRejectedLocale]);

  return { isEditableWhatsAppTemplate };
};
